import { convertToPhoneNumbers } from "components/PhoneInput";
import { BankFormState, CompanyFormState, NameAndAddress, VenueFormState } from "./components/models";
import { findEnumObject } from "utilities/enumutils";
import { findCountryById, findStateById } from "utilities/countrystateutils";

export const convertToNameAndAddress = (companyName: string, address: server.dto.Address): NameAndAddress => ({
  companyName: companyName ?? "",
  addressLine1: address.addressLine1 ?? "",
  addressLine2: address.addressLine2 ?? "",
  city: address.city ?? "",
  postalCode: address.postalCode ?? "",
  state: findStateById(address.stateId),
  country: findCountryById(address.country),
});

export const convertToFormState = (company: server.dto.Company): CompanyFormState => ({
  address: {
    visitingAddress: convertToNameAndAddress(company.name, company.address),
    postalAddress: convertToNameAndAddress(company.admName, company.admAddress),
  },
  profile: {
    isArtist: company.isArtist,
    isPromoter: company.isPromoter,
    isVenue: company.isVenue,
  },
  status: company.status,
  website: company.website ?? "",
  phone: company.landlineNumbers.join(";"),
  facebook: company.facebook ?? "",
  networkPath: company.networkPath ?? "",
  additional: company.additional ?? "",
  financialRisk: company.financialRisk ?? {
    comment: "",
    level: findEnumObject("unknown", serverReference.financialRiskLevels),
  },
  euVatNumber: company.euVatNumber?.number ?? "",
  fiscalNumber1: company.fiscalNumber1 ?? "",
  fiscalNumber2: company.fiscalNumber2 ?? "",
  businessRegistrationNumber: company.businessRegistrationNumber ?? "",
  owner: company.owner,
  internalId: company.internalId ?? "",
  legalStructure: company.legalStructure ?? "",
  expectPurchaseOrderNumber: company.expectPurchaseOrderNumber
});

export const convertToVenueFormState = (venueDetails: server.dto.VenueDetails): VenueFormState => ({
  capacity: venueDetails?.capacity?.value,
  ageRestriction: venueDetails?.ageRestriction ? findEnumObject(venueDetails.ageRestriction, serverReference.ageRestrictions) : findEnumObject("unknown", serverReference.ageRestrictions),
  doors: {
    open: venueDetails?.doors?.open,
    close: venueDetails?.doors?.close,
    formatted: []
  },
  category: venueDetails?.category ? findEnumObject(venueDetails.category, serverReference.venueCategories) : findEnumObject("unknown", serverReference.venueCategories),
  stage: {
    width: venueDetails?.stage?.width,
    depth: venueDetails?.stage?.depth,
    toFloor: venueDetails?.stage?.toFloor,
    toCeiling: venueDetails?.stage?.toCeiling,
    formatted: []
  },
  vehicleAccess: venueDetails?.vehicleAccess ?? "",
  parking: venueDetails?.parking ?? "",
  gearAccess: venueDetails?.gearAccess ?? "",
  power: venueDetails?.power ?? "",
  lighting: venueDetails?.lighting ?? "",
  dressingRooms: venueDetails?.dressingRooms ?? "",
  noiseRestrictions: venueDetails?.noiseRestrictions ?? "",
})

export const convertToBankFormState = (bankDetails: server.dto.CompanyBank): BankFormState => ({
  accountNumber: bankDetails.accountNumber ?? "",
  beneficiaryName: bankDetails.beneficiaryName ?? "",
  swiftCode: bankDetails.swiftCode ?? "",
  sortCode: bankDetails.sortCode ?? "",
  bankAddress: bankDetails.bankAddress ?? "",
});

export const getInstruction = (
  company: server.dto.Company,
  companyForm: CompanyFormState,
  bankForm: BankFormState,
  partial: Partial<server.dto.UpdateCompanyDetailsInstruction>
): server.dto.UpdateCompanyDetailsInstruction => ({
  additional: companyForm.additional,
  administrativeAddress: {
    addressLine1: companyForm.address.postalAddress.addressLine1,
    addressLine2: companyForm.address.postalAddress.addressLine2,
    city: companyForm.address.postalAddress.city,
    postalCode: companyForm.address.postalAddress.postalCode,
    stateId: companyForm.address.postalAddress.state.key
  },
  regularAddress: {
    addressLine1: companyForm.address.visitingAddress.addressLine1,
    addressLine2: companyForm.address.visitingAddress.addressLine2,
    city: companyForm.address.visitingAddress.city,
    postalCode: companyForm.address.visitingAddress.postalCode,
    stateId: companyForm.address.visitingAddress.state.key
  },
  regularName: companyForm.address.visitingAddress.companyName,
  administrativeName: companyForm.address.postalAddress.companyName,
  bankDetails: {
    accountNumber: bankForm.accountNumber,
    beneficiaryName: bankForm.beneficiaryName,
    swiftCode: bankForm.swiftCode,
    sortCode: bankForm.sortCode,
    bankAddress: bankForm.bankAddress
  },
  businessRegistrationNumber: companyForm.businessRegistrationNumber,
  companyId: company.companyId,
  euVatNumber: companyForm.euVatNumber,
  expectPurchaseOrderNumber: companyForm.expectPurchaseOrderNumber,
  facebook: companyForm.facebook,
  financialRisk: {
    comment: companyForm.financialRisk?.comment,
    level: companyForm.financialRisk?.level.value,
  },
  fiscalNumber1: companyForm.fiscalNumber1,
  fiscalNumber2: companyForm.fiscalNumber2,
  geoLocation: company.geoCode?.location ? [company.geoCode.location.lat, company.geoCode.location.lng].join(",") : null,
  googlePlaceId: company.googlePlaceId,
  industryClassification: company.industryClassification,
  internalId: companyForm.internalId,
  isArtist: companyForm.profile.isArtist,
  isPromoter: companyForm.profile.isPromoter,
  isVenue: companyForm.profile.isVenue,
  legalStructure: companyForm.legalStructure,
  networkPath: companyForm.networkPath,
  ownerUserId: companyForm.owner.userId,
  phoneNumbers: convertToPhoneNumbers(companyForm.phone),
  statusId: companyForm.status.statusId,
  website: companyForm.website,
  ...partial,
});

export const getUpdateVenueInstuction = (companyId: number, venue: VenueFormState, partial: Partial<server.dto.SetVenueDetailsInstruction>): server.dto.SetVenueDetailsInstruction => ({
  companyId,
  ageRestriction: venue.ageRestriction?.value,
  capacity: venue.capacity,
  category: venue.category?.value,
  doors: venue.doors ? {
    open: venue.doors.open?.cultureInvariant,
    close: venue.doors.close?.cultureInvariant
  } : undefined,
  dressingRooms: venue.dressingRooms,
  gearAccess: venue.gearAccess,
  lighting: venue.lighting,
  noiseRestrictions: venue.noiseRestrictions,
  parking: venue.parking,
  power: venue.power,
  stage: venue.stage,
  vehicleAccess: venue.vehicleAccess,
  ...partial,
});

export const getUpdateBankAccountInstruction = (company: server.dto.Company, companyFormState: CompanyFormState, bankAccountFormState: BankFormState, partial: Partial<server.dto.UpdateBankAccountInstruction>): server.dto.UpdateCompanyDetailsInstruction => {
  const instruction = getInstruction(company, companyFormState, bankAccountFormState, {});

  return {
    ...instruction,
    bankDetails: {
      ...instruction.bankDetails,
      accountNumber: partial.accountNumber ?? instruction.bankDetails.accountNumber,
      bankAddress: partial.bankAddress ?? instruction.bankDetails.bankAddress,
      beneficiaryName: partial.beneficiaryName ?? instruction.bankDetails.beneficiaryName,
      sortCode: partial.sortCode ?? instruction.bankDetails.sortCode,
      swiftCode: partial.swiftCode ?? instruction.bankDetails.swiftCode,
    }
  }
};